import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { MDBAnimation, MDBBtn } from "mdbreact";
import { useDispatch, useSelector } from "react-redux";
import { PUBLIC_TESTIMONIALS } from "../../../services/redux/slices/marketing/feedback";
import SafeSwiper from "../../../components/swiper/SafeSwiper";
import ShareFeedbackModal from "./shareFeedbackModal";

// Below this many approved testimonials, the dual auto-scrolling slider
// looks sparse/glitchy (Swiper's loop mode expects enough slides to fill
// more than one viewport) — a simple static grid reads better instead.
const MIN_FOR_SLIDER = 6;

// Function to shuffle an array (Fisher-Yates)
const shuffleArray = (arr) => {
  const arrayCopy = [...arr];
  for (let i = arrayCopy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arrayCopy[i], arrayCopy[j]] = [arrayCopy[j], arrayCopy[i]];
  }
  return arrayCopy;
};

const StarRating = ({ rating = 0 }) => (
  <div className="homePage-testimonials-star-rating">
    {[0, 1, 2, 3, 4].map((_, i) => {
      let className = "homePage-testimonials-star";
      if (rating >= i + 1) {
        className += " full";
      } else if (rating >= i + 0.5) {
        className += " half";
      }
      return (
        <span key={i} className={className}>
          ★
        </span>
      );
    })}
  </div>
);

// No photo is ever collected for a testimonial — every card uses a
// neutral, initials-based avatar instead of an image request. Falls back
// to "?" defensively even if the API ever omits initials.
export const TestimonialCard = ({ t }) => (
  <div className="homePage-testiminials-sliderCard">
    <div className="homePage-testimonials-avatar" aria-hidden="true">
      {t.initials || "?"}
    </div>
    <div className="homePage-testimonials-review">
      <StarRating rating={t.rating} />
      <span className="homePage-testimonials-review-text">"{t.comment}"</span>
      <span className="homePage-testimonials-review-name">
        - {t.name}
        {(t.role || t.company) && (
          <span className="homePage-testimonials-review-meta">
            {[t.role, t.company].filter(Boolean).join(", ")}
          </span>
        )}
      </span>
    </div>
  </div>
);

export default function Testimonials() {
  const dispatch = useDispatch();
  const { collections, isLoading } = useSelector(({ feedback }) => feedback);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    dispatch(PUBLIC_TESTIMONIALS());
  }, [dispatch]);

  // Two different shuffled arrays for two sliders
  const shuffledTestimonials1 = useMemo(
    () => shuffleArray(collections),
    [collections]
  );
  const shuffledTestimonials2 = useMemo(
    () => shuffleArray(collections),
    [collections]
  );

  const hasTestimonials = collections?.length > 0;
  const useSlider = collections?.length >= MIN_FOR_SLIDER;

  return (
    <>
      <MDBAnimation reveal type="fadeInDown" duration="1.5s" delay=".5s">
        <h1 className="homePage-testimonials-title mb-5">Testimonials</h1>
      </MDBAnimation>
      <div className="homPage-testimonials-section">
        <div className="homPage-testimonials-container">
          <MDBAnimation
            reveal
            type="fadeInLeft"
            duration="1.5s"
            delay=".5s"
            className="homePage-testimonials-text"
          >
            <h1>We believe in the power of community</h1>
            <p>
              Our goal is to create a product and service that you're satisfied
              with and use it every day. This is why we're constantly working on
              our services to make it better every day and really listen to what
              our users has to say.
            </p>
            <MDBBtn
              color="primary"
              className="homePage-testimonials-cta"
              onClick={() => setShowShareModal(true)}
            >
              Share Pilot Feedback
            </MDBBtn>
          </MDBAnimation>

          <div className="homePage-testimonials-sliderContainer-responsive">
            {!hasTestimonials && !isLoading && (
              <div className="homePage-testimonials-empty">
                <p>
                  We're still gathering feedback from our pilot users. Be the
                  first to share your experience with Pinoy iMD!
                </p>
              </div>
            )}

            {hasTestimonials && !useSlider && (
              <div className="homePage-testimonials-grid">
                {collections.map((t) => (
                  <TestimonialCard t={t} key={t.id} />
                ))}
              </div>
            )}

            {hasTestimonials && useSlider && (
              <>
                <MDBAnimation
                  reveal
                  type="fadeIn"
                  duration="1.5s"
                  delay="1s"
                  className="homePage-testiminials-sliderContainer hide"
                >
                  <SafeSwiper
                    modules={[Autoplay]}
                    direction="vertical"
                    loop={true}
                    speed={5000}
                    autoplay={{
                      delay: 0,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: false,
                    }}
                    allowTouchMove={false}
                    spaceBetween={0}
                    slidesPerView={4}
                    style={{ height: "600px" }}
                  >
                    {shuffledTestimonials1.map((t) => (
                      <SwiperSlide key={t.id}>
                        <TestimonialCard t={t} />
                      </SwiperSlide>
                    ))}
                  </SafeSwiper>
                </MDBAnimation>
                <MDBAnimation
                  reveal
                  type="fadeIn"
                  duration="1.5s"
                  delay="1.5s"
                  className="homePage-testiminials-sliderContainer"
                >
                  <SafeSwiper
                    modules={[Autoplay]}
                    direction="vertical"
                    loop={true}
                    speed={5000}
                    autoplay={{
                      delay: 0,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: false,
                    }}
                    simulateTouch={false}
                    allowTouchMove={false}
                    touchStartPreventDefault={false} // 🔹 importante
                    passiveListeners={false} // 🔹 para sa mga mobile browsers
                    spaceBetween={0}
                    style={{ height: "600px" }}
                    className="swiper-vertical-reverse"
                    breakpoints={{
                      0: { slidesPerView: 5, spaceBetween: 5 },
                      1200: { slidesPerView: 4, spaceBetween: 5 },
                      1600: { slidesPerView: 4, spaceBetween: 5 },
                    }}
                  >
                    {shuffledTestimonials2.map((t) => (
                      <SwiperSlide key={t.id}>
                        <TestimonialCard t={t} />
                      </SwiperSlide>
                    ))}
                  </SafeSwiper>
                </MDBAnimation>
              </>
            )}
          </div>
        </div>
      </div>

      <ShareFeedbackModal
        show={showShareModal}
        toggle={() => setShowShareModal((prev) => !prev)}
      />
    </>
  );
}
